import {
  CANDIDATES_STATUS_SUCCESS,
  PATIENT_TAB_FILES,
  PATIENT_TAB_MAP,
  PATIENT_TAB_PROFILE,
  PATIENT_TAB_REHAB_FORMS,
  PATIENT_TAB_SEARCH,
  PATIENT_TAB_TIMELINE,
  PATIENT_TAB_TRANSITIONAL_CARE,
} from "core/consts";
import { isFilesActivatedAtAuctionLevel } from "core/model/auctions";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { Auction } from "core/types";
import { ACCENT_COLOR } from "ds_legacy/materials/colors";
import { areNewRehabFormsActive } from "dsl/ecosystems/PatientForms/RehabForms/utils";
import { getTransitionalCareAction } from "dsl/ecosystems/PatientForms/TransitionalCare";
import { PRODUCT_TOURS, tourAttributes } from "dsl/molecules/useProductTour";
import { useTranslations } from "translations";
import { PatientTabConfig } from ".";

export function useGetPatientTabConfigs({
  auction,
}: {
  auction: Auction | undefined;
}): PatientTabConfig[] {
  const translations = useTranslations();
  const transitionalCareAction = getTransitionalCareAction(auction);
  const { isRedesignActive } = useIsDocsAndExtractActive();

  if (!auction) {
    return [];
  }

  return [
    {
      label: translations.patient.patientProfile,
      value: PATIENT_TAB_PROFILE,
    },
    {
      disabled: auction?.candidates_status !== CANDIDATES_STATUS_SUCCESS,
      label: translations.search.search,
      tour: tourAttributes({
        stepKey: PRODUCT_TOURS.search_merge_not_started.steps.search_tab.key,
        tourKey: PRODUCT_TOURS.search_merge_not_started.key,
      }),
      value: PATIENT_TAB_SEARCH,
    },
    {
      label: translations.timeline.title,
      tour: tourAttributes({
        stepKey: PRODUCT_TOURS.files_page.steps.documentation_tab.key,
        tourKey: PRODUCT_TOURS.files_page.key,
      }),
      value: PATIENT_TAB_TIMELINE,
    },
    {
      hide: !transitionalCareAction || isRedesignActive,
      label: translations.patientForms.tabTitle,
      value: PATIENT_TAB_TRANSITIONAL_CARE,
    },
    {
      hide: !areNewRehabFormsActive(auction) || isRedesignActive,
      label: translations.insuranceApp.eligibility,
      value: PATIENT_TAB_REHAB_FORMS,
    },
    {
      hide: !isFilesActivatedAtAuctionLevel(auction),
      label: translations.fileSection.tabTitle,
      tour: tourAttributes({
        stepKey: PRODUCT_TOURS.files_page.steps.files_tab.key,
        tourKey: PRODUCT_TOURS.files_page.key,
      }),
      value: PATIENT_TAB_FILES,
    },

    {
      label: translations.patient.map.title,
      value: PATIENT_TAB_MAP,
    },
  ];
}

export const getDotProps = ({
  color,
  disabled,
  value,
}: {
  color: string | null;
  disabled?: PatientTabConfig["disabled"];
  value: PatientTabConfig["value"];
}) => {
  const isSearchStep = value === PATIENT_TAB_SEARCH;

  const isAccentColor = color === ACCENT_COLOR;

  const showDot = !disabled && isSearchStep && isAccentColor;

  return {
    dotColor: color,
    showDot,
  };
};
